import { useEffect, useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { Search, Eye, Pencil, CheckCircle2, RotateCcw, ShieldCheck, Loader2 } from 'lucide-react';
import { IdCardsApi, IdCardTab, IdCardVerificationRow } from '@/services/id-cards.api';
import { AcademicClassesApi } from '@/services/academic.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { Modal } from '@/components/ui/Modal';
import { Input, Select } from '@/components/ui/Input';
import { DataTable, useDataTable } from '@/components/data-table';
import { EditIdCardModal } from './EditIdCardModal';

const columnHelper = createColumnHelper<IdCardVerificationRow>();

/**
 * ID Card Generation — school-tier "Verify ID Cards". Matches the real
 * legacy workflow (survey confirmed): a School Admin locks a student/staff
 * member's card data before it's eligible for the org's bulk print batch.
 * No public preview route anywhere — every preview here goes through the
 * same authenticated API call as the rest of this page.
 */
export function VerifyIdCardsPage() {
  const qc = useQueryClient();
  const [tab, setTab] = useState<IdCardTab>('students');
  const [classId, setClassId] = useState('');
  const dt = useDataTable({ defaultLimit: 20, defaultSortBy: 'name', defaultSortOrder: 'asc' });
  const [previewFor, setPreviewFor] = useState<{ id: string; tab: IdCardTab } | null>(null);
  const [editFor, setEditFor] = useState<string | null>(null);
  const [lockedError, setLockedError] = useState<string | null>(null);

  useEffect(() => {
    dt.onPaginationChange((prev) => ({ ...prev, pageIndex: 0 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, classId]);

  const { data: classesPage } = useQuery({
    queryKey: ['academic-classes', 'all-for-selector'],
    queryFn: () => AcademicClassesApi.list({ limit: 100 }),
    enabled: tab === 'students',
  });
  const classes = classesPage?.items ?? [];

  const { data, isFetching } = useQuery({
    queryKey: ['id-cards', 'verification', tab, classId, dt.params],
    queryFn: () => IdCardsApi.verificationQueue({ tab, classId: tab === 'students' ? classId || undefined : undefined, ...dt.params }),
    placeholderData: keepPreviousData,
  });

  const { data: previewHtml, isFetching: loadingPreview } = useQuery({
    queryKey: ['id-cards', 'preview', previewFor?.id, previewFor?.tab],
    queryFn: () => IdCardsApi.previewHtml(previewFor!.id, previewFor!.tab),
    enabled: !!previewFor,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['id-cards', 'verification'] });

  const verify = useMutation({
    mutationFn: (id: string) => IdCardsApi.verify(id, tab),
    onSuccess: invalidate,
    onError: (e: any) => setLockedError(e?.response?.data?.error?.message ?? 'Could not verify this record.'),
  });

  const unverify = useMutation({
    mutationFn: (id: string) => IdCardsApi.unverify(id, tab),
    onSuccess: invalidate,
    onError: (e: any) => setLockedError(e?.response?.data?.error?.message ?? 'Could not unverify this record.'),
  });

  const bulkVerify = useMutation({
    mutationFn: () => IdCardsApi.bulkVerify({ tab, classId: tab === 'students' ? classId || undefined : undefined }),
    onSuccess: invalidate,
    onError: (e: any) => setLockedError(e?.response?.data?.error?.message ?? 'Could not bulk-verify.'),
  });

  const columns =
    tab === 'students'
      ? [
          columnHelper.accessor('admissionNo', { header: 'Admission No' }),
          columnHelper.accessor('name', { header: 'Name' }),
          columnHelper.display({
            id: 'class',
            header: 'Class · Section',
            cell: (ctx) => (ctx.row.original.className ? `${ctx.row.original.className} · ${ctx.row.original.sectionName}` : '—'),
          }),
          statusColumn,
          actionsColumn(setPreviewFor, setEditFor, verify, unverify, tab),
        ]
      : [
          columnHelper.accessor('name', { header: 'Name' }),
          columnHelper.accessor('designation', { header: 'Designation', cell: (ctx) => ctx.getValue() ?? '—' }),
          statusColumn,
          actionsColumn(setPreviewFor, setEditFor, verify, unverify, tab),
        ];

  return (
    <>
      <PageHeader
        title="Verify ID Cards"
        description="Lock a student's or staff member's card data before it's eligible for the org's print batch."
        actions={
          <button className="btn-primary" onClick={() => bulkVerify.mutate()} disabled={bulkVerify.isPending || !data?.pendingCount}>
            {bulkVerify.isPending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <ShieldCheck className="mr-1.5 h-4 w-4" />}
            Verify &amp; Lock All Pending
          </button>
        }
      />

      {data && (
        <div className="mb-4 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-700">
          <strong>{data.pendingCount}</strong> {tab === 'students' ? 'student' : 'staff'} record{data.pendingCount === 1 ? '' : 's'} pending verification.
        </div>
      )}
      {lockedError && (
        <div className="mb-4 flex items-center justify-between rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
          <span>{lockedError}</span>
          <button onClick={() => setLockedError(null)} className="text-red-400 hover:text-red-600">
            &times;
          </button>
        </div>
      )}

      <div className="mb-4 flex items-center gap-1 border-b border-slate-200">
        {(['students', 'staff'] as const).map((tKey) => (
          <button
            key={tKey}
            onClick={() => setTab(tKey)}
            className={`border-b-2 px-4 py-2 text-sm font-medium ${
              tab === tKey ? 'border-brand-600 text-brand-700' : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            {tKey === 'students' ? 'Students' : 'Staff & Teachers'}
          </button>
        ))}
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          <Input value={dt.searchInput} onChange={(e) => dt.setSearchInput(e.target.value)} placeholder="Search by name" className="pl-9" />
        </div>
        {tab === 'students' && (
          <Select className="!w-44" value={classId} onChange={(e) => setClassId(e.target.value)}>
            <option value="">All Classes</option>
            {classes.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>
        )}
      </div>

      <DataTable columns={columns} result={data} state={dt} loading={isFetching} emptyMessage="No records found." getRowId={(row) => row.id} />

      <Modal open={!!previewFor} onClose={() => setPreviewFor(null)} title="ID Card Preview" size="lg">
        {loadingPreview || !previewHtml ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
          </div>
        ) : (
          <iframe title="ID Card Preview" srcDoc={previewHtml} className="h-[500px] w-full rounded border border-slate-200" />
        )}
      </Modal>

      <EditIdCardModal studentId={editFor} onClose={() => setEditFor(null)} />
    </>
  );
}

const statusColumn = columnHelper.accessor('idCardVerified', {
  header: 'Status',
  cell: (ctx) => (ctx.getValue() ? <Badge tone="green">Verified</Badge> : <Badge tone="amber">Pending</Badge>),
});

function actionsColumn(
  setPreviewFor: (v: { id: string; tab: IdCardTab } | null) => void,
  setEditFor: (id: string | null) => void,
  verify: { mutate: (id: string) => void; isPending: boolean; variables?: string },
  unverify: { mutate: (id: string) => void; isPending: boolean; variables?: string },
  tab: IdCardTab,
) {
  return columnHelper.display({
    id: 'actions',
    header: '',
    cell: (ctx) => (
      <div className="flex items-center gap-2">
        <button
          className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700"
          onClick={() => setPreviewFor({ id: ctx.row.original.id, tab })}
        >
          <Eye className="h-3.5 w-3.5" /> Preview
        </button>
        {tab === 'students' && (
          <button
            className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700"
            onClick={() => setEditFor(ctx.row.original.id)}
          >
            <Pencil className="h-3.5 w-3.5" /> Edit
          </button>
        )}
        {!ctx.row.original.idCardVerified ? (
          <button
            className="inline-flex items-center gap-1 text-xs text-brand-600 hover:text-brand-800 disabled:opacity-50"
            onClick={() => verify.mutate(ctx.row.original.id)}
            disabled={verify.isPending && verify.variables === ctx.row.original.id}
          >
            <CheckCircle2 className="h-3.5 w-3.5" /> Verify
          </button>
        ) : (
          <button
            className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 disabled:opacity-50"
            onClick={() => unverify.mutate(ctx.row.original.id)}
            disabled={unverify.isPending && unverify.variables === ctx.row.original.id}
            title="Reverse this verification — unlocks the record for editing again"
          >
            <RotateCcw className="h-3.5 w-3.5" /> Unverify
          </button>
        )}
      </div>
    ),
  });
}
